import React, {
  cloneElement,
  Children,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { Fade } from '@wds/motion';
import { isBrowser, safeRest, uniqueId } from '@wds/utils';
import { KEYS } from '@wds/helpers';
import { List } from '@wds/list';

import MenuItem from './MenuItem';
import MenuGroup from './MenuGroup';
import useStyles from './useStyles';

export const MENU_PLACEMENTS = [
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
];

export const FOCUS_STATE = Object.freeze({ TOP: 'top', BOTTOM: 'bottom' });

const Menu = ({
  autoClose,
  children,
  dense,
  placement,
  menuTrigger,
  noMaxHeight,
  onChange,
  show: showProp,
  stretched,
  renderTo,
  role,
  noRules,
  ...rest
}) => {
  // Refs
  const menuRef = useRef(null);
  const innerMenuRef = useRef(null);
  const itemsRef = useRef(null);

  // Menu State Items
  const [showState, setShowState] = useState(showProp);
  const [menuStyle, setMenuStyle] = useState({});
  //
  const [focusState, setFocusState] = useState(null);
  const [focusIndex, setFocusIndex] = useState(-1);

  // aria-owns for a11y
  const id = useMemo(() => uniqueId('wds-menu-'), []);

  // Styles
  const theme = useTheme();
  const classes = useStyles({
    menuStyle,
    noMaxHeight,
    placement,
    stretched,
    theme,
  });

  /**
   * a way to help replicate componentWillReceiveProps
   * and componentDidUpdate
   */
  const prevPropsRef = useRef(null);
  useEffect(() => {
    prevPropsRef.current = { showProp, showState };
    /**
     * Each time the MenuItem List is generated through the menu button trigger
     * this code will create a list of accessible ListItems to traverse through.
     */
    if (showState && innerMenuRef.current) {
      itemsRef.current = [
        ...innerMenuRef.current.getElementsByTagName('li'),
      ].map((item) => item.firstChild);
    }
  }, [showProp, showState]);
  const prevProps = prevPropsRef.current;

  // This will auto focus the first or last item
  useEffect(() => {
    if (focusState) {
      const focusInd =
        focusState === FOCUS_STATE.TOP ? 0 : itemsRef.current.length - 1;
      if (itemsRef.current[focusInd]) {
        itemsRef.current[focusInd].focus();
      }
      setFocusIndex(focusInd);
    }
  }, [focusState]);

  /**
   * @todo needs Modal
   * Update the placement of the menu when renderTo is passed in.
   */
  const updatePosition = () => {
    if (menuRef.current && showState && innerMenuRef.current.children.length) {
      const rect = menuRef.current.getBoundingClientRect();
      const innerRect = innerMenuRef.current.getBoundingClientRect();
      const coordinates = {
        'top-left': {
          top: rect.top - innerRect.height,
          left: rect.left,
        },
        'top-right': {
          top: rect.top - innerRect.height,
          left: rect.right - innerRect.width,
        },
        'bottom-left': {
          top: rect.bottom,
          left: rect.left,
        },
      }[placement] || {
        top: rect.bottom,
        left: rect.right - innerRect.width,
      };

      const { top, left } = coordinates;

      setMenuStyle({
        top,
        left,
        zIndex: theme.zIndex.space + 1,
        position: 'fixed',
        right: 'auto', // fixes menu too wide
        bottom: 'auto', // fixes box-shadow not showing
      });
    }
  };

  const handleScroll = () => {
    if (showState && renderTo) {
      updatePosition();
    }
  };

  // componentDidMount
  useEffect(() => {
    if (showState && renderTo) updatePosition();
    // ssr doesn't have window object
    if (isBrowser) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationchange', handleScroll);
    }

    // componentWillUnmount
    return () => {
      if (isBrowser) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('orientationchange', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderTo, showState]);

  // componentDidUpdate
  useEffect(() => {
    if (prevProps) {
      if (!prevProps.showState && showProp && renderTo) {
        updatePosition();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // componentWillReceiveProps
  useEffect(() => {
    setShowState(showProp);
  }, [showProp]);

  const menuChange = (showMenu) => {
    if (!showMenu) {
      setFocusState(null);
      setFocusIndex(-1);
    }
    setShowState(showMenu);
    if (onChange) {
      onChange(showMenu);
    }
  };

  const handleOnClick = (e) => {
    menuChange(!showState);
    e.stopPropagation();
  };

  const handleKeyScroll = (index) => {
    if (itemsRef.current) {
      if (index > -1 && itemsRef.current[index]) {
        const menuItemHeight =
          itemsRef.current[index].offsetTop +
          itemsRef.current[index].offsetHeight;
        const menuContainerHeight = innerMenuRef.offsetHeight;
        innerMenuRef.current.scrollTop =
          menuItemHeight <= menuContainerHeight
            ? 0
            : menuItemHeight - menuContainerHeight;
      }
    }
  };

  const handleOnKeyDown = (e) => {
    const { key } = e;

    if (showState) {
      const itemsLen = itemsRef.current.length - 1;
      const focusInd = focusIndex === -1 ? 0 : focusIndex;
      let newFocusIndex = 0;
      switch (key) {
        case KEYS.TAB:
        case KEYS.ESC:
          menuChange(false);
          if (menuRef.current.firstChild) {
            menuRef.current.firstChild.focus();
          }
          e.preventDefault();
          break;
        case KEYS.ENTER:
        case KEYS.SPACE:
          itemsRef.current[focusInd].click();
          break;
        case KEYS.UP:
          newFocusIndex = focusInd > 0 ? focusInd - 1 : itemsLen;
          break;
        case KEYS.DOWN:
          newFocusIndex = focusInd === itemsLen ? 0 : focusInd + 1;
          break;
        default:
          break;
      }

      if (key === KEYS.UP || key === KEYS.DOWN) {
        setFocusIndex(newFocusIndex);
        handleKeyScroll(newFocusIndex);
        if (newFocusIndex > -1 && itemsRef.current[newFocusIndex]) {
          itemsRef.current[newFocusIndex].focus();
          e.preventDefault();
        }
      }
    }
  };

  const handleOnTriggerKeyDown = (e) => {
    const { key } = e;

    if (menuTrigger.props.onKeyDown) {
      menuTrigger.props.onKeyDown(e);
    }

    const isNotInput =
      menuTrigger.type && menuTrigger.type.displayName !== 'Input';

    if (isNotInput) {
      switch (key) {
        case KEYS.SPACE:
        case KEYS.ENTER:
        case KEYS.DOWN:
          handleOnClick(e);
          setFocusState(FOCUS_STATE.TOP);
          e.preventDefault();
          break;
        case KEYS.UP:
          handleOnClick(e);
          setFocusState(FOCUS_STATE.BOTTOM);
          e.preventDefault();
          break;
        default:
          break;
      }
    }
  };

  const handleAutoCloseBlur = () => {
    setTimeout(() => {
      const isFocusInRenderContainer =
        renderTo && renderTo.contains(document.activeElement);
      const isFocusInMenuRef =
        menuRef.current && menuRef.current.contains(document.activeElement);

      if (!isFocusInRenderContainer && !isFocusInMenuRef) {
        menuChange(false);
      }
    }, 0);
  };

  const handleMenuClose = () => {
    if (autoClose) {
      menuChange(false);
    }
  };

  const renderListItems = (ch, groupindex = 0, handleMenu) =>
    Children.map(ch, (child) => {
      if (!isValidElement(child)) return null;
      return cloneElement(child, {
        groupindex,
        // isFocused,
        onKeyDown: handleOnKeyDown,
        handleMenuClose: handleMenu,
      });
    });

  const renderList = () => {
    const items = Children.map(children, (child, index) => {
      if (!isValidElement(child)) return;
      if (child.type.displayName !== 'MenuGroup') return;
      // eslint-disable-next-line consistent-return
      return (
        <MenuGroup>
          <List interactive noRules dense={dense} {...rest}>
            {renderListItems(child.props.children, index, handleMenuClose)}
          </List>
        </MenuGroup>
      );
    });
    if (items.length) return items;

    return (
      <List interactive dense={dense} noRules={noRules} {...rest}>
        {children && renderListItems(children, 0, handleMenuClose)}
      </List>
    );
  };

  const renderMenu = () => {
    const MenuComponent = (
      <Fade in={showState} speed="fast">
        <>
          <div className={classes.wrenchMenu} role={role} id={id}>
            <div className={classes.wrenchMenuBody} ref={innerMenuRef}>
              {renderList()}
            </div>
          </div>
        </>
      </Fade>
    );

    if (renderTo) {
      return ReactDOM.createPortal(MenuComponent, renderTo);
    }
    return MenuComponent;
  };

  const renderTrigger = () => {
    const componentName = menuTrigger.type.displayName;
    const isButton = componentName === 'Button';
    const isInput = componentName === 'Input';

    return cloneElement(menuTrigger, {
      'aria-haspopup': role,
      'aria-expanded': !isInput ? showState : undefined,
      ...(showState && { 'aria-owns': id }),
      ...(isButton && { ariaPressed: showState || showProp }),
      onFocus: isInput ? handleOnClick : undefined,
      onClick: !isInput ? handleOnClick : undefined,
      onKeyDown: handleOnTriggerKeyDown,
      tabIndex: 0,
    });
  };

  return (
    <span
      className={classes.wrenchMenuContainer}
      onBlur={autoClose ? handleAutoCloseBlur : undefined}
      ref={menuRef}
      tabIndex={-1}
      {...safeRest(rest)}
    >
      {renderTrigger()}
      {renderMenu()}
    </span>
  );
};

Menu.propTypes = {
  /**
   * If true, Menu will close automatically on click away
   */
  autoClose: PropTypes.bool,
  /**
   * Composed of Menu.Item
   */
  children: PropTypes.node.isRequired,
  /**
   * Reduce child Menu.Item height and font-weight
   */
  dense: PropTypes.bool,
  /**
   * You can anchor your menu to any corner on the north or south of the element that summoned it.
   */
  placement: PropTypes.oneOf(MENU_PLACEMENTS),
  /**
   * Required prop that will open or close the menu
   */
  menuTrigger: PropTypes.node.isRequired,
  /**
   * If true, Menu will not limit a max height of 325px
   */
  noMaxHeight: PropTypes.bool,
  /**
   * onChange handler
   * function(event: object) => void
   */
  onChange: PropTypes.func,
  /**
   * If true, menu items are displayed
   */
  show: PropTypes.bool,
  /**
   *
   */
  stretched: PropTypes.bool,
  /**
   *  You may want to render the menu attached to a parent element so that it can be rendered outside of the trigger container.
   */
  renderTo: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.elementType }),
  ]),
  /**
   * Define menu ARIA role
   */
  role: PropTypes.string,
  /**
   * Remove divider lines between List.Item
   */
  noRules: PropTypes.bool,
};

Menu.defaultProps = {
  autoClose: true,
  dense: false,
  placement: undefined,
  noMaxHeight: false,
  onChange: undefined,
  stretched: false,
  show: false,
  renderTo: undefined,
  role: 'menu',
  noRules: false,
};

Menu.Item = MenuItem;
Menu.Group = MenuGroup;

export default Menu;
